/**
 * Shared value objects.
 *
 * The vocabulary every module speaks. Kept deliberately small: anything that
 * belongs to one module's domain lives in that module, and anything here
 * becomes a coupling point for all of them.
 *
 * Master plan §3 K5.
 */

/** The Organization — the legal entity, and the isolation boundary. */
export type TenantId = string & { readonly __tenantId: unique symbol };

/** A trading location. Authorization scope, never an isolation boundary. */
export type VenueId = string & { readonly __venueId: unique symbol };

/**
 * A brand is first-class and distinct from a venue: one kitchen may serve
 * several virtual brands, each with its own catalogue projection, listings,
 * hours and aggregator store ids, while sharing one KDS, one inventory pool,
 * one roster and one capacity ceiling.
 *
 * Modelling brand as an attribute of venue instead of an entity is a rewrite
 * waiting to happen the first time a tenant opens a delivery-only brand.
 */
export type BrandId = string & { readonly __brandId: unique symbol };

/** A revenue centre within a venue: bar, restaurant, terrace, room service. */
export type RevenueCentreId = string & { readonly __revenueCentreId: unique symbol };

/**
 * The trading day, "YYYY-MM-DD", derived from the venue's business_day_start.
 *
 * Not a calendar date: a restaurant's Tuesday ends at 04:00 on Wednesday. Sales
 * and labour must derive this identically or every per-day metric is wrong.
 */
export type BusinessDate = string & { readonly __businessDate: unique symbol };

/**
 * A VAT rate resolved from the regulatory registry and SNAPSHOTTED onto a line
 * at ring time.
 *
 * The rate is a function of (tax category, consumption mode, venue type,
 * jurisdiction, date) — never a property of the product. Carrying the source
 * and the resolution date means a receipt can be re-explained years later
 * without re-running the rule engine against rules that have since changed.
 */
export interface ResolvedTaxRate {
  readonly rateBp: number;
  readonly sourceRef: string;
  readonly resolvedAt: Date;
}

/** How a supply is consumed. Drives tax resolution and kitchen routing. */
export type ConsumptionMode = "DINE_IN" | "TAKEAWAY" | "DELIVERY";

/** Where the guest came from. Drives pricing, tax and reconciliation. */
export type OrderChannel =
  | "POS"
  | "QR"
  | "KIOSK"
  | "ONLINE"
  | "AGGREGATOR"
  | "EVENT"
  | "ROOM_CHARGE";

/**
 * The four reversal types, which are genuinely four things and not one.
 *
 *   VOID   — never produced, or produced in error. Reverses inventory AND
 *            revenue.
 *   COMP   — the guest received it and we are not charging. Consumes
 *            inventory, zero revenue, hits a comp expense account.
 *   DISCOUNT — a price reduction. Consumes inventory, partial revenue, and it
 *            reduces the VAT base.
 *   REFUND — money returned after capture, typically on a later business day.
 *
 * Storing a comp as a 100 % discount is the single most common modelling
 * mistake in this domain and it permanently destroys food-cost and
 * loss-prevention analytics: you can no longer answer "how much food did we
 * give away that was actually produced?"
 */
export type ReversalKind = "VOID" | "COMP" | "DISCOUNT" | "REFUND";

/** Who authorised something, and why. Required on every reversal. */
export interface Authorization {
  readonly actorId: string;
  readonly reasonCode: string;
  readonly note?: string;
  /** Set when the action exceeded a threshold requiring manager approval. */
  readonly approvedBy?: string;
}

// --- Allergens -------------------------------------------------------------

/**
 * Annex II of Regulation (EU) 1169/2011. All fourteen, always.
 *
 * HERE RATHER THAN IN A MODULE because three of them needed it and each had its
 * own spelling: catalog owned the real list, kitchen declared
 * `type AllergenCode = string` — which accepts anything, including a
 * misspelling that renders as no allergen at all — and the event boundary
 * needed to validate against something. A module cannot import another module's
 * internals, and rightly, so the shared vocabulary belongs in the contract.
 *
 * It is a closed EU-wide list, not a jurisdiction rule, so it does not belong
 * in the regulatory registry either: there is nothing here to branch on.
 */
export const ALLERGENS = [
  "GLUTEN",
  "CRUSTACEANS",
  "EGGS",
  "FISH",
  "PEANUTS",
  "SOYBEANS",
  "MILK",
  "NUTS",
  "CELERY",
  "MUSTARD",
  "SESAME",
  "SULPHITES",
  "LUPIN",
  "MOLLUSCS",
] as const;

export type AllergenCode = (typeof ALLERGENS)[number];

/**
 * What a guest filters a menu by. A CLAIM the venue makes about a dish, unlike
 * an allergen, which is a duty: "vegan" helps somebody choose, while a missing
 * GLUTEN on an allergen list can hurt them. Kept apart for exactly that reason
 * — a dietary tag never stands in for an allergen and is never derived from one.
 */
export const DIETARY_TAGS = ["VEGAN", "VEGETARIAN", "GLUTEN_FREE", "LACTOSE_FREE", "SPICY"] as const;

export type DietaryTag = (typeof DIETARY_TAGS)[number];

/**
 * The languages a menu's words may be translated into. The venue writes its
 * menu in its own language; these are the others a guest may read it in.
 */
export const CONTENT_LANGUAGES = ["en", "sk", "uk", "de"] as const;

export type ContentLanguage = (typeof CONTENT_LANGUAGES)[number];

/** A dish's (or a category's, or a choice's) words in other languages. */
export type Translations = Readonly<Partial<Record<ContentLanguage, { readonly name?: string; readonly description?: string }>>>;

/**
 * FOUR-VALUED, and the fourth value is the point.
 *
 * "We have no spec sheet for this ingredient" and "this ingredient is free from
 * celery" are different facts. A boolean collapses them into the dangerous one.
 */
export type AllergenState = "CONTAINS" | "MAY_CONTAIN" | "FREE_FROM" | "UNKNOWN";

/**
 * How money arrives, as a closed set.
 *
 * HERE RATHER THAN IN A MODULE for the same reason as the allergen list above,
 * and the reason arrived the same way: `accounting` needs to split a day's
 * takings by payment form and cannot import `orders` to learn what the forms
 * are. Modules may not reach into each other, rightly, so the shared vocabulary
 * belongs in the contract.
 *
 * NOT A JURISDICTION RULE. Which of these is an evidovaná tržba IS one — the
 * Czech test is decided by CONTACT rather than by instrument, so cash, card at
 * a terminal, QR, meal vouchers and gift cards are all in scope when paid in
 * person and out of it at a distance. That branch lives in the regulatory
 * registry; this is only the vocabulary it branches on.
 *
 * `HOUSE_ACCOUNT`, `ROOM_CHARGE` and `EXTERNAL` are where our kinds and the
 * statute's test come apart, and that is recorded as open rather than guessed.
 */
export const TENDER_KINDS = [
  "CASH",
  "CARD",
  "MEAL_VOUCHER",
  "GIFT_CARD",
  "HOUSE_ACCOUNT",
  "QR_TRANSFER",
  "VOUCHER",
  "ROOM_CHARGE",
  "EXTERNAL",
  /**
   * Paid through an online gateway before the guest arrived — an online pickup
   * order. AT A DISTANCE BY DEFINITION, which is why it has its own kind: the
   * Czech test is contact, and this one never has any (the fiscal projection
   * leaves it out of what is reported).
   */
  "ONLINE",
] as const;

export type TenderKind = (typeof TENDER_KINDS)[number];

/**
 * A modifier as it was chosen on a line: "bez cibule", "hranolky místo knedlíku".
 *
 * Frozen at ring time with the menu's own wording, the way the price and the
 * allergens are, so the cook reads what the server chose even after the menu moves.
 * Here rather than in a module because the order and the kitchen both carry it.
 */
export interface LineModifier {
  readonly groupId: string;
  readonly optionId: string;
  readonly name: string;
  /** At least 1. "Extra slanina ×2" is a quantity, not two rows. */
  readonly quantity: number;
  /** Gross, per unit of the line, for this quantity. Negative for "bez přílohy". */
  readonly priceDeltaMinor: bigint;
}

/** What the kitchen needs of a modifier: the words, and how many. */
export interface TicketModifier {
  readonly name: string;
  readonly quantity: number;
}

/** A kitchen note is a sentence, not a letter. Longer than this is a phone call. */
export const LINE_NOTE_MAX = 140;
